import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';

import { setPortal } from 'modules/portal';

import { makeRowColumn, getHeadObj } from 'lib/commonLib';
import { userOnLogin } from 'lib/api/user';
import { campaignManageList, campaignManageImgList } from 'lib/api/campaign';

import Icon from 'components/atoms/Icon';
import NewButton from 'components/molecules/NewButton';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';
import NewTableContainer from 'components/molecules/NewTable';

import Modal from './Modal';

const useStyles = makeStyles((theme) => ({
  circle: {
    width: 15,
    height: 15,
    margin: '0 auto',
    borderRadius: '50%',
  },
}));

const Statistics = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  const dispatch = useDispatch();
  const [initialization, setInitialization] = useState(false);
  const initParams = {
    sort: 'desc',
    order: 'CONTACT_NAME',
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    advrtsPrdtCode: '01',
    advrtsTpCode: '05',
    contact: 'all',
    itlTpCode: '01,03,04,05,08,09,10,12,13,98,99',
  };
  const [params, setParams] = useState(initParams);
  const [reFetchHead, setReFetchHead] = useState(false);

  const initList = {
    state: [
      { label: '전체', value: 'all' },
      { label: '온라인', value: '01' },
      { label: '모바일', value: '02' },
      { label: '중지', value: '03' },
    ],
    image: [
      { label: '전체', value: 'all' },
      { label: '등록', value: '01' },
      { label: '미등록', value: '02' },
    ],
    campDesc: [
      { label: '전체', value: 'all' },
      { label: '등록', value: '01' },
      { label: '미등록', value: '02' },
    ],
    ctrState: [
      { label: '전체', value: 'all' },
      { label: '정상', value: '01' },
      { label: '하위30%', value: '02' },
    ],
  };
  const [list] = useState(initList);

  const initSnackbar = {
    open: false,
    variant: '',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);
  const initModal = {
    open: false,
    type: null,
    params: {},
    data: [],
  };
  const [modal, setModal] = useState(initModal);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  useEffect(() => {
    if (reFetchHead === true) {
      setReFetchHead(false);
    } // eslint-disable-next-line
  }, [reFetchHead]);

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    let tempParams = { ...newParams };
    delete tempParams['pltfomTpCode'];
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      if (tempParams.advrtsTpCode === '06') {
        tempParams = {
          ...tempParams,
          table: 'keyword',
        };
      } else if (tempParams.advrtsTpCode === '26') {
        tempParams = {
          ...tempParams,
          table: 'category',
        };
      } else if (tempParams.advrtsTpCode === '28') {
        tempParams = {
          ...tempParams,
          table: 'audience',
        };
      } else if (tempParams.advrtsPrdtCode === '07') {
        tempParams = {
          ...tempParams,
          table: 'native',
        };
      } else {
        delete tempParams['table'];
      }
      if (tempParams.advrtsPrdtCode === '07') {
        if (newParams.state === undefined) {
          tempParams.state = 'all';
        }
        if (newParams.image === undefined) {
          tempParams.image = 'all';
        }
        if (newParams.campDesc === undefined) {
          tempParams.campDesc = 'all';
        }
        if (newParams.ctrState === undefined) {
          tempParams.ctrState = 'all';
        }
      } else {
        delete tempParams['state'];
        delete tempParams['image'];
        delete tempParams['campDesc'];
        delete tempParams['ctrState'];
      }
      setReFetchHead(true);
      setParams(tempParams);
    }
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    IMG_URL: {
      makeHead: (headsMap) => {
        return <>이미지</>;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          !!rowObj['IMG_URL'] && (
            <NewButton
              title={t('전체이미지')}
              icon="faImages"
              iconBtn
              onClick={(e) => handleModalOpen(rowObj['SITE_CODE'])}
              disableRipple
              style={{ backgroundColor: '#79bd2f' }}
              color="#fff"
            />
          )
        );
      },
    },
    STATE: {
      makeBody: (rowObj, headsMap) => {
        let color;
        if (rowObj['STATE'] === '01') {
          color = theme.palette.primary.main;
        } else if (rowObj['STATE'] === '02') {
          color = theme.palette.common.gray;
        }
        return (
          <div className={classes.circle} style={{ backgroundColor: color }} />
        );
      },
    },
    W_STATE: {
      makeBody: (rowObj, headsMap) => {
        return (
          <Icon
            icon="faPowerOff"
            style={
              rowObj.W_STATE === '01'
                ? { color: '#79bd2f' }
                : { color: '#6d6d6d' }
            }
          />
        );
      },
    },
    M_STATE: {
      makeBody: (rowObj, headsMap) => {
        return (
          <Icon
            icon="faPowerOff"
            style={
              rowObj.M_STATE === '01'
                ? { color: '#79bd2f' }
                : { color: '#6d6d6d' }
            }
          />
        );
      },
    },
    CONTACT_NAME: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CONTACT_NAME');
        return makeRowColumn(
          rowObj['CONTACT_NAME'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
      bodyStyle: { textAlign: 'center' },
    },
    IMAGE_URL_17: { merged: true },
    IMAGE_URL_22: { merged: true },
    CAMP_GENDER: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('이벤트 속성')} type="head">
            <div style={{ width: 100 }}>
              {getHeadObj(headsMap, 'CAMP_GENDER').label}
            </div>
            <div style={{ width: 150 }}>
              {getHeadObj(headsMap, 'CAMP_AGE').label}
            </div>
            {getHeadObj(headsMap, 'CAMP_TAG').label !== undefined && (
              <>
                <div style={{ width: 250 }}>
                  {getHeadObj(headsMap, 'CAMP_TAG').label}
                </div>
                <div style={{ width: 250 }}>
                  {getHeadObj(headsMap, 'CAMP_CATE').label}
                </div>
              </>
            )}
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CAMP_GENDER');
        const bodyItem2 = getHeadObj(headsMap, 'CAMP_AGE');
        const bodyItem3 = getHeadObj(headsMap, 'CAMP_TAG');
        const bodyItem4 = getHeadObj(headsMap, 'CAMP_CATE');
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {makeRowColumn(
                rowObj['CAMP_GENDER'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
            <div style={{ width: 150 }}>
              {makeRowColumn(
                rowObj['CAMP_AGE'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </div>
            {getHeadObj(headsMap, 'CAMP_TAG').label !== undefined && (
              <>
                <div style={{ width: 250 }}>
                  {makeRowColumn(
                    rowObj['CAMP_TAG'],
                    bodyItem3.type,
                    bodyItem3.format
                  )}
                </div>
                <div style={{ width: 250 }}>
                  {makeRowColumn(
                    rowObj['CAMP_CATE'],
                    bodyItem4.type,
                    bodyItem4.format
                  )}
                </div>
              </>
            )}
          </SpanningTableContents>
        );
      },
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    CAMP_AGE: { merged: true },
    CAMP_TAG: { merged: true },
    CAMP_CATE: { merged: true },
    TRGT_GENDER: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('목표타켓팅 설정')} type={'head'}>
            <div style={{ width: 100 }}>타겟 성별</div>
            <div style={{ width: 150 }}>타겟 나이</div>
            <div style={{ width: 250 }}>타겟 카테고리</div>
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'TRGT_GENDER');
        const bodyItem2 = getHeadObj(headsMap, 'TRGT_AGE');
        const bodyItem3 = getHeadObj(headsMap, 'TRGT_CATE');
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {makeRowColumn(
                rowObj['TRGT_GENDER'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
            <div style={{ width: 150 }}>
              {makeRowColumn(
                rowObj['TRGT_AGE'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </div>
            <div style={{ width: 250 }}>
              {makeRowColumn(
                rowObj['TRGT_CATE'],
                bodyItem3.type,
                bodyItem3.format
              )}
            </div>
          </SpanningTableContents>
        );
      },
      headStyle: { borderRight: '1px solid #e5e8eb' },
      bodyStyle: { borderRight: '1px solid #e5e8eb' },
    },
    TRGT_AGE: { merged: true },
    TRGT_CATE: { merged: true },
    RGN_TRGT_USE_YN: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('지역타겟팅 설정')} type={'head'}>
            <div style={{ width: 50 }}>
              {getHeadObj(headsMap, 'RGN_TRGT_USE_YN').label}
            </div>
            <div style={{ width: 50 }}>
              {getHeadObj(headsMap, 'ADSTRD_CODE_CNT').label}
            </div>
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADSTRD_CODE_CNT');
        return (
          <SpanningTableContents>
            <div style={{ width: 50 }}>
              {rowObj['RGN_TRGT_USE_YN'] === '01' ? 'Y' : 'N'}
            </div>
            <div style={{ width: 50 }}>
              {makeRowColumn(
                rowObj['ADSTRD_CODE_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
          </SpanningTableContents>
        );
      },
      headStyle: { borderRight: '1px solid #e5e8eb' },
      bodyStyle: { borderRight: '1px solid #e5e8eb' },
    },
    ADSTRD_CODE_CNT: { merged: true },
    ADC_CAMP_CNT: {
      makeHead: (headsMap) => {
        return (
          <SpanningTableContents topTitle={t('카테고리 설정')} type={'head'}>
            <div style={{ width: 80 }}>
              {getHeadObj(headsMap, 'ADC_CAMP_CNT').label}
            </div>
            <div style={{ width: 130 }}>
              {getHeadObj(headsMap, 'ADC_STR').label}
            </div>
          </SpanningTableContents>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADC_STR');
        return (
          <SpanningTableContents>
            <div style={{ width: 80 }}>{rowObj['ADC_CAMP_CNT']}</div>
            <div style={{ width: 130 }}>
              {makeRowColumn(
                rowObj['ADC_STR'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
          </SpanningTableContents>
        );
      },
      headStyle: { borderRight: '1px solid #e5e8eb' },
      bodyStyle: { borderRight: '1px solid #e5e8eb' },
    },
    ADC_STR: { merged: true },
    DC_FRME_YN: {
      makeBody: (rowObj, headsMap) => {
        return rowObj['DC_FRME_YN'] === '01' ? 'Y' : 'N';
      },
    },
    W_URL: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['W_URL'] ? (
          <NewButton
            title={t('사이트로 이동')}
            iconBtn
            icon="faHome"
            disableRipple
            onClick={(e) => handlePortalUrl(rowObj['W_URL'])}
          />
        ) : (
          <span style={{ color: theme.palette.common.red }}>{t('미등록')}</span>
        );
      },
    },
    M_URL: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['M_URL'] ? (
          <NewButton
            title={t('사이트로 이동')}
            iconBtn
            icon="faHome"
            disableRipple
            onClick={(e) => handlePortalUrl(rowObj['M_URL'])}
          />
        ) : (
          <span style={{ color: theme.palette.common.red }}>{t('미등록')}</span>
        );
      },
    },
    EVENT_URL: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['EVENT_URL'] ? (
          <NewButton
            title={t('사이트로 이동')}
            iconBtn
            icon="faHome"
            disableRipple
            onClick={(e) => handlePortalUrl(rowObj['EVENT_URL'])}
          />
        ) : (
          <span style={{ color: theme.palette.common.red }}>{t('미등록')}</span>
        );
      },
    },
    SITE_CODE: { merged: true },
    CAMP_DESC_STR: {
      makeBody: (rowObj, headsMap) => {
        let texts = '';
        if (rowObj['CAMP_DESC_STR'] !== null) {
          texts = rowObj['CAMP_DESC_STR'].split('||');
        } else {
          texts = rowObj['CAMP_DESC_STR'];
        }
        return (
          <IconAreaWrap
            text={texts.map((text, index) => {
              return (
                <span key={index}>
                  {text}
                  <br />
                </span>
              );
            })}
            icon="faSignInAlt"
            toolTipTitle={t('광고주 로그인')}
            onClick={(e) => handleOnLogin(rowObj)}
          />
        );
      },
      headStyle: { width: 400 },
    },
    PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return t('캠페인') + 'CTR';
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'CLICK_CNT');
        const bodyItem3 = getHeadObj(headsMap, 'CLICK_RATE');
        return (
          <>
            <span style={{ color: theme.palette.common.black }}>
              {makeRowColumn(
                rowObj['PAR_EPRS_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
              <br />
              {makeRowColumn(
                rowObj['CLICK_CNT'],
                bodyItem2.type,
                bodyItem2.format
              )}
              <br />
            </span>
            {makeRowColumn(
              rowObj['CLICK_RATE'],
              bodyItem3.type,
              bodyItem3.format
            )}
          </>
        );
      },
    },

    CLICK_CNT: { merged: true },
    CLICK_RATE: { merged: true },
    ADVER_ID: { merged: true },
  };

  /**
   * 로그인처리
   */
  const handleOnLogin = (rowData) => {
    userOnLogin(
      rowData['ADVER_ID'],
      'manage',
      'https://manage.mobon.net/advertiser/form?' +
        'siteCode=' +
        rowData['SITE_CODE'] +
        '&svcType=' +
        params.advrtsPrdtCode
    )
      .then((response) => {
        const { data, success } = response.data;
        if (success) {
          handlePortalUrl(data.url);
        } else {
          alert('로그인에 실패했습니다. 관리자에게 문의하세요.');
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (data) => {
    let newModal = { ...initModal };
    campaignManageImgList({ siteCode: data })
      .then((response) => {
        const { data } = response.data;
        newModal = {
          ...newModal,
          open: true,
          data: data,
        };
      })
      .catch((error) => {
        setSnackbar({
          ...initSnackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        setModal({
          ...newModal,
        });
      });
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal = { ...modal };
    newModal = {
      ...newModal,
      open: false,
    };
    setModal(newModal);
  };

  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    setModal(initModal);
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(initSnackbar);
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          usePltfomTpCodeFilter={false}
          useManagerFilter
          selectOnlyOne={{
            displayFilter: true,
          }}
          onlyProducts
          useDisplayAll={false}
        />
        {params.advrtsPrdtCode === '07' && (
          <>
            <FilterCollection
              label={t('게재상태')}
              name="state"
              params={params}
              value={params.state}
              type="radio"
              list={list.state}
              callBack={getParam}
              labelKey="label"
              valueKey="value"
            />
            <FilterCollection
              label={t('이미지')}
              name="image"
              params={params}
              value={params.image}
              type="radio"
              list={list.image}
              callBack={getParam}
              labelKey="label"
              valueKey="value"
            />
            <FilterCollection
              label={t('캠페인 공통 설명')}
              name="campDesc"
              params={params}
              value={params.campDesc}
              type="radio"
              list={list.campDesc}
              callBack={getParam}
              labelKey="label"
              valueKey="value"
            />
            <FilterCollection
              label={t('CTR 상태 필터')}
              name="ctrState"
              params={params}
              value={params.ctrState}
              type="radio"
              list={list.ctrState}
              callBack={getParam}
              labelKey="label"
              valueKey="value"
            />
          </>
        )}
      </NewFilterWrap>

      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={campaignManageList}
        customized={customized}
        useTotal={false}
        useSearch={false}
        reFetchHead
      />

      <Modal
        modal={modal}
        handleSubmit={handleSubmit}
        handleModalClose={handleModalClose}
      />
      <SnackbarWrap
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={handleSnackBarClose}
      />
    </>
  );
};

export default Statistics;
